/**
 * 「本月三件事」内核 —— **E 域 E2 的唯一算法实现**（`AC-E2.15`）
 *
 * 依据：规格 **§15** · `DR-014` 决策四 · `DR-086`（第 7 项阈值定稿）·
 * `E域PRD` **§7.2** `AC-E2.2`–`AC-E2.6` / `AC-E2.10`。
 *
 * ## 🔴 本文件住两件事，且都只在这里
 *
 * | 能力 | 消费方 |
 * |---|---|
 * | `rankInsights()` —— 打分 / 剔零 / 同权按金额 / 取前 3 / 标记 `isKey` | E2 卡片（本批）· **「家庭财务日」第 3 问**（`AC-E2.15`：将来复用同一函数，禁各写一套） |
 * | `detectRecurringCandidates()` —— 疑似周期支出的**判定**（`DR-086`） | E2 候选池第 6 项 |
 *
 * ## 🔴 打分公式（`AC-E2.2`）
 *
 * ```
 * score = actionability × severityWeight
 * actionability = 0 ⇒ 直接不入池（连排序都不参与）
 * severityWeight：最高 = 3 · 高 = 2 · 中 = 1
 * 同权重 ⇒ 金额影响（cents）大者优先（AC-E2.4）
 * 取前 3（AC-E2.5）；不足 3 条只给已有的（AC-E2.6 —— 宁缺毋滥，不凑数）
 * ```
 *
 * ⚠️ 因为 `actionability ∈ {0,1}`，`score` 与 `severityWeight` **同序** ——
 * 公式仍是三档权重乘 1，不是"权重就是分数"的巧合：将来若引入"半可行动"（如 0.5），
 * 只需把 `InsightCandidate.actionability` 的类型放宽，**排序代码一行不改**。
 *
 * ## 🔴 排序必须**稳定**
 *
 * 同权重同金额时按**候选池的声明序**（调用方决定）：这让"同一份数据两次请求给出同样顺序"
 * 成为构造性的，而不是靠 `Array.prototype.sort` 的实现细节。
 */

import type { Cents } from '../types/brand.js';
import { addDays } from './calendar.js';
// 🔴 `inclusiveDayCount` 住 `nucleus/trend.ts`（不是 `calendar.ts`）—— 它是"含两端的自然日数"，
//    与日历算术同族但归属统计口径那一侧。
import { inclusiveDayCount } from './trend.js';

// ─────────────────────────── 枚举与权重 ───────────────────────────

/**
 * 7 项候选（`AC-E2.1`）。
 *
 * 🔴 **枚举住内核、契约引用它** —— 与 `nucleus/upcoming.ts` 的 `UpcomingKind` 同一处置
 * （方向是"契约 ← 内核"，反过来会让内核依赖契约层）。
 */
export const InsightKind = {
  /** ① 可花为负（J3 · `DR-012`） */
  SpendableNegative: 'spendableNegative',
  /** ② 预算偏离（G 域判档 · `DR-011`） */
  BudgetDeviation: 'budgetDeviation',
  /** ③ 异常大额（K2 σ 离群 · `AC-K2.15`） */
  Anomaly: 'anomaly',
  /** ④ 分类偏离（K2 分类占比 + 上一期同分类） */
  CategoryDrift: 'categoryDrift',
  /** ⑤ 目标节奏落后（I 域 `pace`） */
  GoalBehind: 'goalBehind',
  /** ⑥ 疑似周期支出（`DR-086`） */
  RecurringHint: 'recurringHint',
  /** ⑦ 待缴未付（F2 聚合的 `pending` 子集 · `DR-011` §4） */
  PendingPayable: 'pendingPayable',
} as const;
export type InsightKind = (typeof InsightKind)[keyof typeof InsightKind];

/**
 * 严重度三档（`AC-E2.3` 定稿）：
 * - **最高** = 可花为负 / 预算**已超**；
 * - **高** = 异常大额 / 分类偏离 / **预算 ≥80%**；
 * - **中** = 目标落后 / 疑似周期 / 待缴未付。
 */
export const InsightSeverity = {
  Highest: 'highest',
  High: 'high',
  Medium: 'medium',
} as const;
export type InsightSeverity = (typeof InsightSeverity)[keyof typeof InsightSeverity];

/** 权重表（`AC-E2.3`）—— **唯一实现**，端侧不得再写一份 */
export const SEVERITY_WEIGHT: Readonly<Record<InsightSeverity, number>> = {
  [InsightSeverity.Highest]: 3,
  [InsightSeverity.High]: 2,
  [InsightSeverity.Medium]: 1,
};

/** 池上限（`AC-E2.5`：取前 3） */
export const MONTHLY_INSIGHTS_LIMIT = 3;

// ─────────────────────────── 打分与取前 3 ───────────────────────────

/**
 * 候选（服务端组装好的**一条**，带该条自己的取数快照）。
 *
 * `actionability` 只取 `0 | 1`：`0` = **这条没有明确动作**（`AC-E2.9`）⇒ 不入池。
 * ⚠️ 服务端**不该**把不可行动的条目塞进来再指望内核剔除 —— 但内核仍然判，
 * 因为这是"不得入选"的唯一执行点（同 `AC-E2.2` 的措辞：可行动性为 0 **直接不入池**）。
 */
export interface InsightCandidate<D> {
  readonly severity: InsightSeverity;
  readonly actionability: 0 | 1;
  /** 金额影响（本币整数分）—— 同权重时的排序键（`AC-E2.4`） */
  readonly amountCents: Cents;
  readonly data: D;
}

/** 一条入选结果（`isKey` 由本函数标记 —— `AC-E2.10` 的唯一执行点） */
export interface RankedInsight<D> {
  readonly severity: InsightSeverity;
  readonly amountCents: Cents;
  readonly isKey: boolean;
  readonly data: D;
}

/**
 * 打分 → 剔除不可行动 → 排序 → 取前 `limit`。
 *
 * @param candidates 候选池（**声明序即最终的同分兜底序**）
 * @param limit 上限，默认 3（`AC-E2.5`）
 * @returns 至多 `limit` 条；`isKey` 只给第 1 条（`AC-E2.10`）。
 *          🔴 **不足 `limit` 条就返回几条** —— 绝不补占位（`AC-E2.6`）。
 */
export function rankInsights<D>(
  candidates: readonly InsightCandidate<D>[],
  limit: number = MONTHLY_INSIGHTS_LIMIT,
): RankedInsight<D>[] {
  if (limit <= 0) return [];

  // 1) 剔零：`actionability = 0` 连排序都不参与（`AC-E2.2`）
  const scored: { candidate: InsightCandidate<D>; index: number; score: number }[] = [];
  candidates.forEach((candidate, index) => {
    if (candidate.actionability === 0) return;
    scored.push({ candidate, index, score: SEVERITY_WEIGHT[candidate.severity] });
  });

  // 2) 排序：score 降序 → 金额降序 → **声明序**（稳定，见文件头）
  scored.sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    const amtA = a.candidate.amountCents as number;
    const amtB = b.candidate.amountCents as number;
    if (amtA !== amtB) return amtB - amtA;
    return a.index - b.index;
  });

  // 3) 取前 limit 条，第 1 条标 `isKey`（不凑数 —— 有几条给几条）
  return scored.slice(0, limit).map((entry, i) => ({
    severity: entry.candidate.severity,
    amountCents: entry.candidate.amountCents,
    isKey: i === 0,
    data: entry.candidate.data,
  }));
}

// ─────────────────────── 疑似周期支出（`DR-086`） ───────────────────────

/**
 * 判定参数 —— **7 个值全部来自 `DR-086` §2**，一个都不许在调用处硬编码。
 *
 * 🔴 它必须是**共享常量**：`AC-E2.15` 要求「家庭财务日」第 3 问与 E2 共用同一套判定。
 */
export const RECURRING_HINT_POLICY = {
  /** ① 观察窗口：近 90 天（含今天，滚动） */
  windowDays: 90,
  /** ② 最少出现次数：≥ 3（按"消费日"计） */
  minOccurrences: 3,
  /** ③ 间隔容差：月付 25–35 天 / 周付 6–8 天（**两种形态不混判**） */
  monthlyGap: [25, 35] as const,
  weeklyGap: [6, 8] as const,
  /** ④ 金额容差：与**中位数**相差 ≤ 5%（`500` 个基点） */
  amountToleranceBps: 500,
  /** 输出上限：最多 3 条（与总池上限同量级，避免一张卡被"疑似周期"占满） */
  maxHints: 3,
} as const;

/** 一条待判定的支出腿（**服务端只负责取数**，判据全在本文件） */
export interface RecurringExpenseRow {
  /** 账本本地日 `YYYY-MM-DD` */
  readonly day: string;
  /** 原币整数分（**正数**；本项只判支出） */
  readonly amountCents: Cents;
  /** 腿所属账户币种 —— 🔴 金额只与**同币种**比（`DR-004`：不跨币相加/相比） */
  readonly currency: string;
  /** 账户 —— 🔴 `DR-086` §6：同一商户在两个账户消费**分开**判 */
  readonly accountId: string;
  readonly categoryId: string | null;
  /** 分类 L2 展示名（归组键退到分类时用它当 `label`） */
  readonly categoryLabel: string;
  /** 原始备注（本函数内部归一化成商户键；不做 IO） */
  readonly note: string | null;
  /** 非空 = 该笔由周期规则生成 ⇒ 排除（`DR-086` §2-7 ①） */
  readonly sourceRuleId: string | null;
  /** `true` = 由分期/摊销生成的腿或带归因列 ⇒ 排除（§2-7 ②） */
  readonly hasAttribution: boolean;
  /** `true` = 转账 / 不在统计内 / 收入腿 ⇒ 排除（§2-7 ③④） */
  readonly excludeFromStats: boolean;
}

/** 一条候选（输出 —— 端侧直接可用，不再算） */
export interface RecurringHint {
  /** 商户名；无商户时 = 分类 L2 名 */
  readonly label: string;
  /** 出现次数（**消费日**数，同日多笔已合并） */
  readonly occurrences: number;
  /** 典型金额 = 组内**中位数**（整数分） */
  readonly typicalAmountCents: Cents;
  /** 下次预计日 = 最后一次 + 中位间隔（仅展示，不落库） */
  readonly nextExpectedDay: string;
  /** 归组键（"去计划页新建"的预填依据） */
  readonly groupKey: string;
}

/**
 * 备注 → 商户键（`DR-086` §2-5 的"归一化"）。
 *
 * 🔴 **宁可归一不足，不可过度归一** —— 把两家不同的店合成一家，会造出一条
 * 看起来很有道理的假周期（这正是 `DR-086` 说"误报代价不对称"的场景）。
 * 因此只做四件事：全半角折叠 → 小写 → 去空白 → 去掉**括号备注与门店后缀**。
 */
export function normalizeMerchantKey(note: string | null | undefined): string {
  if (note === null || note === undefined) return '';
  const folded = note.normalize('NFKC').toLowerCase();
  // 括号里的内容多为门店/分店/备注 ⇒ 先整体去掉再比
  const withoutParen = folded.replace(/[（(][^）)]*[）)]/gu, '');
  const withoutSpace = withoutParen.replace(/\s+/gu, '');
  // 🔴 后缀表里**没有裸「店」**：它会把「全家便利店」剥成「全家便利」（两家不同品牌会撞键）。
  //    只剥"门店形态"的复合后缀 —— 宁可归一不足，不可过度归并。
  const withoutSuffix = withoutSpace.replace(/(旗舰店|专卖店|分店|门店)$/u, '');
  // 去掉尾部标点（"超市。" 与 "超市" 是同一家）
  return withoutSuffix.replace(/[-_·.,，。!！?？、]+$/gu, '');
}

/**
 * 归组键 —— 🔴 **服务端与内核必须用同一个函数**（否则"已在周期规则里"的排除会失效）。
 *
 * 形如 `acct|CNY|m:星巴克` 或 `acct|CNY|c:cat-food-l2`。
 * 商户优先、退分类（`DR-086` §2-5：**一次只用一个键，不混**）。
 */
export function recurringGroupKey(input: {
  accountId: string;
  currency: string;
  merchantKey: string;
  categoryId: string | null;
}): string {
  const body =
    input.merchantKey === ''
      ? `c:${input.categoryId ?? ''}`
      : `m:${input.merchantKey}`;
  return `${input.accountId}|${input.currency}|${body}`;
}

/** 一次"消费日"（同日多笔已合并） */
interface DayBucket {
  day: string;
  total: Cents;
  count: number;
}

/**
 * 判定"疑似周期支出"（`DR-086` §3 的 6 步）—— **纯函数**，无 IO、无当前时间。
 *
 * @param rows 近 `windowDays` 天的支出腿（服务端已按窗口取数；**窗口外的行仍会被本函数挡掉**）
 * @param existingKeys 已存在于周期规则的分组键（用 {@link recurringGroupKey} 算好传入）
 * @param today 账本本地今天（用来卡窗口下界；不传则以 `rows` 里的最大日为基准 —— 便于单测）
 * @returns 0–`maxHints` 条，按典型金额降序
 *
 * ## 🔴 它只出**建议**
 *
 * 不自动建规则、不改任何流水（`DR-086` §3 末段）。调用方的动作只有一个：
 * 「去计划页新建（带预填）」。
 */
export function detectRecurringCandidates(
  rows: readonly RecurringExpenseRow[],
  existingKeys: ReadonlySet<string>,
  today?: string,
): RecurringHint[] {
  const policy = RECURRING_HINT_POLICY;
  const windowStart = today === undefined ? null : addDays(today, -(policy.windowDays - 1));

  // 1) 排除 + 归组（§2-7 / §2-5）
  const groups = new Map<
    string,
    { label: string; currency: string; days: Map<string, DayBucket> }
  >();

  for (const row of rows) {
    // §2-7 ① 已由周期规则生成
    if (row.sourceRuleId !== null) continue;
    // §2-7 ② 分期 / 摊销 / 归因腿
    if (row.hasAttribution) continue;
    // §2-7 ③④ 转账 / 不计收支 / 收入腿
    if (row.excludeFromStats) continue;
    // 只判支出：金额非正的一律不看
    if ((row.amountCents as number) <= 0) continue;
    // 窗口下界（传了 today 才卡；纯单测可以不传）
    if (windowStart !== null && row.day < windowStart) continue;

    const merchantKey = normalizeMerchantKey(row.note);
    const groupKey = recurringGroupKey({
      accountId: row.accountId,
      currency: row.currency,
      merchantKey,
      categoryId: row.categoryId,
    });
    if (existingKeys.has(groupKey)) continue;

    let group = groups.get(groupKey);
    if (group === undefined) {
      group = {
        // §2-5：商户优先，无商户退到分类 L2 名
        label: merchantKey === '' ? row.categoryLabel : row.note?.trim() ?? '',
        currency: row.currency,
        days: new Map<string, DayBucket>(),
      };
      groups.set(groupKey, group);
    }
    // §2-6 同日合并：同一分组同一天的多笔合并为 1 次，金额取当日合计
    const bucket = group.days.get(row.day);
    if (bucket === undefined) {
      group.days.set(row.day, { day: row.day, total: row.amountCents, count: 1 });
    } else {
      bucket.total = ((bucket.total as number) + (row.amountCents as number)) as Cents;
      bucket.count += 1;
    }
  }

  const hints: RecurringHint[] = [];
  for (const [groupKey, group] of groups) {
    // 2) 按日期排序 → 得到 n 个"消费日"
    const days = [...group.days.values()].sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
    // 3) 至少 3 次（§2-2）
    if (days.length < policy.minOccurrences) continue;

    // 4) 间隔：全部落同一档（§2-3 —— 两种形态不混判）
    const gaps: number[] = [];
    let monotonic = true;
    for (let i = 1; i < days.length; i += 1) {
      const prev = days[i - 1];
      const cur = days[i];
      if (prev === undefined || cur === undefined) {
        monotonic = false;
        break;
      }
      const gap = inclusiveDayCount(prev.day, cur.day) - 1;
      if (gap <= 0) {
        monotonic = false;
        break;
      }
      gaps.push(gap);
    }
    if (!monotonic || gaps.length === 0) continue;
    const allIn = (range: readonly [number, number]): boolean =>
      gaps.every((g) => g >= range[0] && g <= range[1]);
    if (!allIn(policy.monthlyGap) && !allIn(policy.weeklyGap)) continue;

    // 5) 金额：每笔（每日合计）与**中位数**相差 ≤ 500 个基点（§2-4）
    const amounts = days.map((d) => d.total as number).sort((a, b) => a - b);
    const median = amounts[medianIndex(amounts.length)];
    if (median === undefined || median <= 0) continue;
    const tolerance = median * policy.amountToleranceBps;
    const withinTolerance = amounts.every((a) => {
      const diff = a >= median ? a - median : median - a;
      return diff * 10_000 <= tolerance;
    });
    if (!withinTolerance) continue;

    // 典型金额 = 中位数；下次预计日 = 最后一次 + 中位间隔（偶数个取靠前者）
    const sortedGaps = [...gaps].sort((a, b) => a - b);
    const medianGap = sortedGaps[medianIndex(sortedGaps.length)] ?? sortedGaps[0] ?? 0;
    const lastDay = days[days.length - 1]?.day ?? '';

    hints.push({
      label: group.label,
      occurrences: days.length,
      typicalAmountCents: median as Cents,
      nextExpectedDay: addDays(lastDay, medianGap),
      groupKey,
    });
  }

  // 6) 排序取前 3（`AC-E2.4` 同一条判据：金额影响大者优先）
  hints.sort((a, b) => (b.typicalAmountCents as number) - (a.typicalAmountCents as number));
  return hints.slice(0, policy.maxHints);
}

/**
 * 中位下标 —— **偶数个取靠前者**（纯离散，不做除法：规格 §22.1 禁浮点）。
 *
 * 这条规则同时用于「金额中位数」与「中位间隔」：两处必须同一处置，
 * 否则 `amounts.length` 与 `gaps.length` 奇偶不同时会给出两套"中位数"。
 *
 * 🔴 等价于 `floor((length − 1) / 2)`，但**不能用 `/` 与 `Math.floor`**（lint 禁令）
 * ⇒ 用减法循环求"最大的 `i` 使 `2i ≤ length − 1`"。下标量级 ≤ 数百，循环代价可忽略。
 */
function medianIndex(length: number): number {
  if (length <= 0) return 0;
  let i = 0;
  while ((i + 1) * 2 <= length - 1) i += 1;
  return i;
}

/** 供端侧核对：这条是否该着色为"预警"（朱红）—— 只有「最高」档算预警（铁律 8：红 = 预警） */
export function isAlarmingSeverity(severity: InsightSeverity): boolean {
  return severity === InsightSeverity.Highest;
}
